import React, { useEffect, useState } from "react";
import { Typography, Divider } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import PaymentIcon from "@mui/icons-material/Payment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Order,
  OrderDetails as OrderDetailsProps,
} from "../../dto/orderSummaryTypes";
import Loader from "../../common/Loader";
import * as Styled from "./OrderConfirmationStyles";
import { fetchOrderDetails } from "../../api/orderFinalAPI";

const OrderConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const orderId = location.pathname.split("/").pop();
  const orgId = localStorage.getItem("orgId");

  const [orderDetails, setOrderDetails] = useState<OrderDetailsProps | null>(
    null
  );

  useEffect(() => {
    const getOrderDetails = async () => {
      if (!orderId) return; // guard for undefined

      try {
        const data = await fetchOrderDetails(orderId);
        if (data?.data) {
          setOrderDetails(data.data);
        }
      } catch (error) {
        console.error("Failed to fetch order details", error);
      }
    };

    getOrderDetails();
  }, [orderId]);

  const handlePayment = () => {
    navigate(`/maltora-customer/my-orders/${orderId}/payment`);
  };

  const handleBack = () => {
    const from = location.state?.from;
    if (from === "cart") {
      navigate(`/maltora-customer/cart`);
    } else {
      navigate(-1);
    }
  };

  if (!orderDetails) {
    return <Loader show={true} />;
  }

  const shippingAddress =
    orderDetails.shippingAddress || orderDetails.billingAddress;
  const { orders } = orderDetails;

  const exciseBreakdown = orderDetails.payment.exciseBreakdown;
  const serviceTotal = orderDetails.payment.serviceTotal || 0;
  const exciseTotal = exciseBreakdown
    ? exciseBreakdown.grandTotal
    : orderDetails.payment.exciseTotal || 0;
  const total = exciseTotal + serviceTotal;

  return (
    <Styled.MainContainer>
      {/* Header Section */}
      <Styled.HeaderContainer>
        <Styled.HeaderTitle>Complete Payment</Styled.HeaderTitle>
        <ArrowBackIcon sx={Styled.BackIconStyle} onClick={handleBack} />
      </Styled.HeaderContainer>

      {/* Info Section */}
      <Styled.InfoSection>
        <InfoIcon sx={{ marginRight: 1 }} />
        <Styled.InfoText>
          Please process the payment to confirm your order.
        </Styled.InfoText>
      </Styled.InfoSection>

      {/* Main Content Layout */}
      <Styled.MainContentLayout>
        {/* LEFT: Product List */}
        <Styled.ProductListContainer>
          {orders.map((order: Order, index: number) => (
            <Styled.ProductListItem
              key={index}
              sx={{
                borderBottom:
                  index !== orders.length - 1 ? "1px solid #E0E0E0" : "none",
              }}
            >
              <Styled.ProductImage
                src={order.image ?? undefined}
                alt={order.name}
              />
              <Styled.ProductTextBox>
                <Styled.ProductTitle>{order.name}</Styled.ProductTitle>
                <Styled.ProductSubText>
                  Quantity: {order.quantity} x {order.liters} ={" "}
                  {order.quantity * order.liters} L
                </Styled.ProductSubText>
              </Styled.ProductTextBox>
            </Styled.ProductListItem>
          ))}
        </Styled.ProductListContainer>

        {/* RIGHT: Summary Card */}
        <Styled.SummaryCard>
          <Styled.AddressSection>
            <Styled.AddressSectionTitle>
              SHIPPING ADDRESS
            </Styled.AddressSectionTitle>

            <Styled.AddressName>{shippingAddress?.name}</Styled.AddressName>
            <Styled.AddressText>{shippingAddress?.address}</Styled.AddressText>

            <Styled.ContactSectionTitle>EMAIL</Styled.ContactSectionTitle>
            <Typography>{shippingAddress?.email}</Typography>

            <Styled.ContactSectionTitle>PHONE</Styled.ContactSectionTitle>
            <Typography>{shippingAddress?.phone}</Typography>
          </Styled.AddressSection>

          {exciseBreakdown && (
            <>
              <Typography sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
                Excise Invoice Breakdown
              </Typography>
              <SummaryRow
                label="Order Amount"
                value={`Rs. ${exciseBreakdown.orderAmount.toFixed(2)}`}
              />
              <SummaryRow
                label={`Excise Duty (Rs. ${exciseBreakdown.exciseDutyRatePerLitre}/L)`}
                value={`Rs. ${exciseBreakdown.exciseDuty.toFixed(2)}`}
              />
              <SummaryRow
                label={`Additional Excise Duty (${exciseBreakdown.additionalExciseDutyPercentage}%)`}
                value={`Rs. ${exciseBreakdown.additionalExciseDuty.toFixed(2)}`}
              />
              <SummaryRow
                label="Sub Total"
                value={`Rs. ${exciseBreakdown.subTotal.toFixed(2)}`}
              />
              <SummaryRow
                label={`TCS (${exciseBreakdown.tcsPercentage}%)`}
                value={`Rs. ${exciseBreakdown.tcs.toFixed(2)}`}
              />
              <SummaryRow
                label="Excise Total"
                value={`Rs. ${exciseBreakdown.grandTotal.toFixed(2)}`}
                bold
              />
            </>
          )}

          <Divider sx={{ my: 2 }} />

          <SummaryRow
            label="Service Invoice Total"
            value={`Rs. ${serviceTotal.toFixed(2)}`}
            bold
          />
          <SummaryRow
            label="Grand Total"
            value={`Rs. ${total.toFixed(2)}`}
            bold
          />

          <Styled.PaymentNote>
            Please process the payment to confirm your order
          </Styled.PaymentNote>

          <Styled.ProceedToPaymentButton
            onClick={handlePayment}
            startIcon={<PaymentIcon fontSize="small" />}
          >
            Proceed to Payment
          </Styled.ProceedToPaymentButton>
        </Styled.SummaryCard>
      </Styled.MainContentLayout>
    </Styled.MainContainer>
  );
};

type SummaryRowProps = {
  label: string;
  value: string;
  bold?: boolean;
  green?: boolean;
};

const SummaryRow = ({
  label,
  value,
  bold = false,
  green = false,
}: SummaryRowProps) => (
  <Styled.SummaryRowContainer>
    <Styled.SummaryLabel bold={bold}>{label}</Styled.SummaryLabel>
    <Styled.SummaryValue bold={bold} green={green}>
      {value}
    </Styled.SummaryValue>
  </Styled.SummaryRowContainer>
);

export default OrderConfirmation;
